<template>
    <div class="automation-template-card">
        <!-- Card default content -->
        <div class="automation-template-card__default-content">
            <header class="automation-template-card__header">
                <CompassTooltip
                    :content="template.connectorTooltip"
                    placement="bottom-start"
                    :arrow="false"
                    :disabled="!template.connectorTooltip"
                >
                    <div class="automation-template-card__connector">
                        <slot name="connector"></slot>
                    </div>
                </CompassTooltip>
            </header>
            <h3 class="typographies-heading-h3 automation-template-card__title">
                {{ template.title }}
            </h3>
            <footer v-if="template.isPopular" class="automation-template-card__footer">
                <CompassTag color="yellow" size="small">
                    {{ translations.labelFlag }}
                </CompassTag>
            </footer>
        </div>

        <!-- Card hover content -->
        <div class="automation-template-card__hover-content">
            <p class="automation-template-card__description typographies-paragraph-medium-regular">
                {{ template.description }}
            </p>
            <CompassButton
                :label="translations.useTemplate"
                class="automation-template-card__button"
                :loading="isLoading"
                @click="handleCreateAutomationWithTemplate"
            />
        </div>
    </div>
</template>

<script>
import { useAutomationTemplate } from 'api/automation/automationService.js';

import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');

export default {
    props: {
        template: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            translations: {
                labelFlag: '🔥 ' + this.$t.__('common.labels.popular').toUpperCase(),
                useTemplate: this.$t.__('automation.AutomationTemplateCard.useTemplate'),
            },
            isLoading: false,
        };
    },
    computed: {
        ...mapStateSystem(['errorsHandler']),
    },
    methods: {
        handleCreateAutomationWithTemplate() {
            this.isLoading = true;
            useAutomationTemplate(this.template.id)
                .then((response) => {
                    this.$router.push({
                        name: 'automations.flow.builder',
                        params: { id: response.data.id },
                    });
                })
                .catch((error) => {
                    this.errorsHandler.handleError(error);
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('common.labels.toast.error'),
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-template-card {
    height: 184px;
    display: flex;
    flex-direction: column;
    border-radius: var(--spacing-0);
    padding: var(--spacing-4);
    background: var(--colors-common-background-default);
    position: relative;
    overflow: hidden;

    &:hover {
        automation-template-card__default-content {
            opacity: 0;
            transform: translateY(-10px);
            pointer-events: none;
        }

        .automation-template-card__footer {
            opacity: 0;
            transform: translateY(-10px);
            pointer-events: none;
        }

        .automation-template-card__hover-content {
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
        }
    }

    &__connector {
        display: flex;
        gap: var(--spacing-1);
    }

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 0 0 var(--spacing-3);
    }

    &__footer {
        position: absolute;
        bottom: 24px;
        left: 16px;
        transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
        z-index: 1;
    }

    &__title {
        margin: 0 0 var(--spacing-0);
    }

    &__description {
        color: var(--colors-common-text-subtle-default);
        margin: 0 0 var(--spacing-3);
    }

    &__default-content,
    &__hover-content {
        transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
    }

    &__hover-content {
        opacity: 0;
        transform: translateY(10px);
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        background: var(--colors-common-background-default);
        padding: var(--spacing-4);
        pointer-events: none;
    }

    &__button {
        position: absolute;
        bottom: 24px;
    }
}
</style>
